import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { IoMdClose } from 'react-icons/io';
import debounce from 'lodash/debounce';
import { toWords } from 'number-to-words';
import CommonSalesOrderFields from '../CommonSalesOrderFields';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';

function CreateAraiMfgIntSalesOrder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [quotationno, setquotationno] = useState('');
  const [quotationdate, setquotationdate] = useState('');
  const [amendmentno, setAmendmentno] = useState('');
  const [amendmentdate, setAmendmentdate] = useState('');
  const [customerpono, setCustomerpono] = useState('');
  const [customerpodate, setCustomerpodate] = useState('');
  const [ldclause, setLdclause] = useState('');
  const [customermaterialcode, setcustomermaterialcode] = useState('');
  const [deliveryterm, setdeliveryterm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [commonFormData, setCommonFormData] = useState({});

  // Calculation state
  const [packingandforwardingpercent, setpackingandforwardingpercent] = useState(0);
  const [freightpercent, setfreightpercent] = useState(0);
  const [gstpercent, setgstpercent] = useState(18);
  const [calculationSummary, setCalculationSummary] = useState({
    nettotal: 0,
    pAndF: 0,
    freight: 0,
    subtotal: 0,
    gst: 0,
    grandtotalinr: 0
  });

  const unitOptions = [
    { value: "", label: "Select UOM" },
    { value: "bot", label: "Bot" },
    { value: "ctn", label: "Ctn" },
    { value: "days", label: "Days" },
    { value: "ft", label: "Ft" },
    { value: "hrs", label: "Hrs" },
    { value: "kg", label: "Kg" },
    { value: "ltr", label: "Ltr" },
    { value: "mtr", label: "Mtr" },
    { value: "nos", label: "Nos" },
    { value: "pil", label: "Pil" },
    { value: "pkt", label: "Pkt" },
    { value: "set", label: "Set" },
    { value: "pcs", label: "Pcs" }
  ];



  const [sono, setsono] = useState('');
  const [sodate, setsodate] = useState('');
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);


  const checksono = async (number) => {
    if (!number) {
      setEnquiryValidationMsg('');
      setIsEnquiryValid(false);
      return;
    }


    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}salesOrder/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg('❌ This sales order number already exists.');
        setIsEnquiryValid(false);
      } else if (response.data === false) {
        setEnquiryValidationMsg('✅ This sales order number is available.');
        setIsEnquiryValid(true);
      } else {
        setEnquiryValidationMsg('Unexpected response from server.');
        setIsEnquiryValid(false);
      }
    } catch (error) {
      console.error("Error checking enquiry number:", error);
      setEnquiryValidationMsg('❌ Error validating sales order number.');
      setIsEnquiryValid(false);
    }
  };


  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checksono(sono);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [sono]);




  // Function to format numbers with commas
  const formatNumberWithCommas = (num) => {
    if (num === null || num === undefined) return '0.00';
    return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Function to convert number to words with Rupees Only suffix
  const numberToWordsWithRupees = (num) => {
    const amount = Math.round(num);
    return `${toWords(amount)} Rupees Only`;
  };

  const handleCommonFormData = (data) => {
    setCommonFormData(data);
    if (data.customerDetails) {
      setfreightpercent(parseFloat(data.customerDetails.freight) || 0);
      setpackingandforwardingpercent(parseFloat(data.customerDetails.packingandforwarding) || 0);
    }
  };

  useEffect(() => {
    calculateTotals();
  }, [rows, packingandforwardingpercent, freightpercent, gstpercent, commonFormData.customerDetails]);

  const calculateTotals = () => {
    const nettotal = rows.reduce((sum, row) => {
      const quantity = parseFloat(row.quantity) || 0;
      const unitrate = parseFloat(row.unitrate) || 0;
      return sum + (quantity * unitrate);
    }, 0);

    const pAndF = nettotal * (packingandforwardingpercent / 100);
    const freight = nettotal * (freightpercent / 100);
    const subtotal = nettotal + pAndF + freight;
    const gst = subtotal * (gstpercent / 100);
    const grandtotalinr = subtotal + gst;

    setCalculationSummary({
      nettotal,
      pAndF,
      freight,
      subtotal,
      gst,
      grandtotalinr
    });  
  };

  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(encodeURIComponent(inputValue || " "));
  }, 300);

  const getProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );

      const products = response.data?.Index || response.data?.data || [];
      const mapped = products.map(product => ({
        value: product.id,
        label: `${product.description}  - ${product.serialnumber} - ${product.partno}`,
        description: product.description,
        itemcode: product.itemcode,
        hsnsacno: product.hsnsacno,
        partno: product.partno,
        serialnumber: product.serialnumber,
        modelnumber: product.modelnumber,
        tagnumber: product.tagnumber,
        moc: product.moc,
        uom: product.unitofmeasurement || unitOptions[0].value,
      }));

      setProductOptions(mapped);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    getProductData();
  }, [productSearch]);

  const handleProductChange = (selectedOption) => {
    if (!selectedOption) return;
    setSelectedProduct(selectedOption);

    const newRow = {
      description: selectedOption.description,
      value: selectedOption.value,
      label: selectedOption.label,
      quantity: '',
      unitrate: '',
      amount: 0,
      partno: selectedOption.partno,
      hsnsacno: selectedOption.hsnsacno,
      itemcode: selectedOption.itemcode,
      serialnumber: selectedOption.serialnumber,
      modelnumber: selectedOption.modelnumber,
      tagnumber: selectedOption.tagnumber,
      moc: selectedOption.moc,
      uom: selectedOption.uom || unitOptions[0].value,
      productremarks: "",
    };

    setRows([...rows, newRow]);
  };

  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;

    if (key === 'quantity' || key === 'unitrate') {
      const quantity = parseFloat(updatedRows[index].quantity) || 0;
      const unitrate = parseFloat(updatedRows[index].unitrate) || 0;
      updatedRows[index].amount = quantity * unitrate;
    }

    setRows(updatedRows);
  };

  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!isEnquiryValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid sales order number before submitting.',
      });
      return;
    }
    const {
      customer,
      customerDetails,
      customerMultipleAddress,
      branch,
      warehouse,
    } = commonFormData;

    if (!customer || !customerMultipleAddress) {
      Swal.fire({
        icon: 'warning',
        title: 'Please fill the required details!',
        text: 'All fields marked with an asterisk are required.',
      });
      return;
    }

    const salesOrderItems = rows.map(row => ({
      product: { id: row.value },
      quantity: row.quantity,
      unitrate: row.unitrate,
      amount: row.amount,
      itemcode: row.itemcode || '',
      hsnsacno: row.hsnsacno || '',
      partno: row.partno || '',
      uom: row.uom || '',
      serialnumber: row.serialnumber || '',
      modelnumber: row.modelnumber || '',
      tagnumber: row.tagnumber || '',
      productremarks: row.productremarks || '',
      moc: row.moc || '',
    }));

    const formData = {
      salesOrderItems,
      salesordertype: "Pump Spare",
      salesordercategory: "Domestic",
      customer: { id: customer.value },
      contactno: customerDetails.contactno,
      contactperson: customerDetails.contactperson,
      email: customerDetails.email,
      customeraddress: { id: customerMultipleAddress?.value },
      branch: branch ? { id: commonFormData.branch.value } : null,
      warehouse: warehouse ? { id: commonFormData.warehouse.value } : null,
      sodate,
      sono,
      customermaterialcode,
      deliveryterm,
      quotationno,
      quotationdate,
      amendmentno,
      amendmentdate,
      customerpono,
      customerpodate,
      ldclause,
      packingandforwardingpercent,
      freightpercent,
      gstpercent,
      nettotal: calculationSummary.nettotal,
      grandtotalinr: calculationSummary.grandtotalinr,
      freight: customerDetails.freight,
      packingandforwarding: customerDetails.packingandforwarding,
      paymentterm: customerDetails.paymentterm
    };

    try {
      await axios.post(`${BASE_URL}salesOrder/save`, formData, {
        headers: authHeader()
      });
      Swal.fire({ icon: 'success', title: 'Success', text: ' Added successfully!' });
      navigate("/salesOrderIndexTable/1");
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed .' });
    }
  };

  return (
    <div className="container">
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Sales Order Number: *</Form.Label>
              <Form.Control
                type="text"
                value={sono}
                onChange={e => {
                  setsono(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !isEnquiryValid) ||
                  enquiryValidationMsg.includes('exists') ||
                  enquiryValidationMsg.includes('Error') ||
                  enquiryValidationMsg.includes('allowed')
                }
                required
              />
              {enquiryValidationMsg && (
                <Form.Control.Feedback type={
                  enquiryValidationMsg.includes('exists') ||
                    enquiryValidationMsg.includes('Error') ||
                    enquiryValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {enquiryValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid enquiry number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Sales Order Date</Form.Label>
              <Form.Control
                type="date"
                value={sodate}
                onChange={(e) => setsodate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Quotation No.:</Form.Label>
              <Form.Control
                type="text"
                value={quotationno}
                onChange={(e) => setquotationno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Quotation Date</Form.Label>
              <Form.Control
                type="date"
                value={quotationdate}
                onChange={(e) => setquotationdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="amendmentno">
              <Form.Label>Amendment No.</Form.Label>
              <Form.Control
                type="text"
                value={amendmentno}
                onChange={(e) => setAmendmentno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="amendmentdate">
              <Form.Label>Amendment Date</Form.Label>
              <Form.Control
                type="date"
                value={amendmentdate}
                onChange={(e) => setAmendmentdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="customerpono">
              <Form.Label>Customer PO No.</Form.Label>
              <Form.Control
                type="text"
                value={customerpono}
                onChange={(e) => setCustomerpono(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="customerpodate">
              <Form.Label>Customer PO Date</Form.Label>
              <Form.Control
                type="date"
                value={customerpodate}
                onChange={(e) => setCustomerpodate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="ldclause">
              <Form.Label>LD Clause</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={ldclause}
                onChange={(e) => setLdclause(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <CommonSalesOrderFields
          onDataChange={handleCommonFormData}
        />
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Customer Material code</Form.Label>
              <Form.Control
                type="text"
                value={customermaterialcode}
                onChange={(e) => setcustomermaterialcode(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Delivery Term</Form.Label>
              <Form.Control
                type="text"
                value={deliveryterm}
                onChange={(e) => setdeliveryterm(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="productSelect">
          <Form.Label>Select a Product</Form.Label>
          <Select
            value={selectedProduct}
            onInputChange={handleProductSearchChange}
            onChange={handleProductChange}
            options={productOptions}
            placeholder="Search or select product"
            isClearable
          />
        </Form.Group>

        <div className="table-responsive mt-3">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Product Description</th>
                <th>Serial No.</th>
                <th>Model No.</th>
                <th>Tag No.</th>
                <th>Part No.</th>
                <th>MOC</th>
                <th>HSN Code</th>
                <th>Product Code</th>
                <th>Quantity</th>
                <th>UOM</th>
                <th>Unit Rate</th>
                <th>Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    {row.description}
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={row.productremarks}
                      onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                      placeholder="Remarks"
                    />
                  </td>
                  <td>{row.serialnumber}</td>
                  <td>{row.modelnumber}</td>
                  <td>{row.tagnumber}</td>
                  <td>{row.partno}</td>
                  <td>{row.moc}</td>
                  <td>{row.hsnsacno}</td>
                  <td>{row.itemcode}</td>
                  <td>
                    <Form.Control
                      type="number"
                      value={row.quantity}
                      onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                    />
                  </td>
                  <td>
                    <Select
                      className='uom'
                      value={unitOptions.find(option => option.value === row.uom)}
                      onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                      options={unitOptions}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={row.unitrate}
                      onChange={(e) => handleRowChange(index, 'unitrate', e.target.value)}
                    />
                  </td>
                  <td>{formatNumberWithCommas(row.amount)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeRow(index)}
                    >
                      <IoMdClose />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <hr />
        <br />
        {/* <div className="table-responsive mt-3">
          <h4>Amount Calculation</h4>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Description</th>
                <th>Percentage</th>
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Net Total</td>
                <td>-</td>
                <td>{formatNumberWithCommas(calculationSummary.nettotal)}</td>
              </tr>
              <tr>
                <td>Packing & Forwarding % :</td>
                <td>
                  <Form.Control
                    type="number"
                    value={packingandforwardingpercent}
                    onChange={(e) => setpackingandforwardingpercent(e.target.value)}
                    style={{ width: '80px' }}
                  />
                  {commonFormData.customerDetails?.packingandforwarding &&
                    <small className="text-muted"> (From customer: {commonFormData.customerDetails.packingandforwarding}%)</small>
                  }
                </td>
                <td>{formatNumberWithCommas(calculationSummary.pAndF)}</td>
              </tr>
              <tr>
                <td>Freight % :</td>
                <td>
                  <Form.Control
                    type="number"
                    value={freightpercent}
                    onChange={(e) => setfreightpercent(e.target.value)}
                    style={{ width: '80px' }}
                  />
                  {commonFormData.customerDetails?.freight &&
                    <small className="text-muted"> (From customer: {commonFormData.customerDetails.freight}%)</small>
                  }
                </td>
                <td>{formatNumberWithCommas(calculationSummary.freight)}</td>
              </tr>
              <tr>
                <td>Sub Total :</td>
                <td>-</td>
                <td>{formatNumberWithCommas(calculationSummary.subtotal)}</td>
              </tr>
              <tr>
                <td>GST % :</td>
                <td>
                  <Form.Control
                    type="number"
                    value={gstpercent}
                    onChange={(e) => setgstpercent(e.target.value)}
                    style={{ width: '80px' }}
                  />
                </td>
                <td>{formatNumberWithCommas(calculationSummary.gst)}</td>
              </tr>
              <tr>
                <td><strong>Grand Total :</strong></td>
                <td>-</td>
                <td><strong>{formatNumberWithCommas(calculationSummary.grandtotalinr)}</strong></td>
              </tr>
              <tr>
                <td colSpan="2"><strong>Grand Total in Words</strong></td>
                <td><em>{numberToWordsWithRupees(calculationSummary.grandtotalinr)}</em></td>
              </tr>
            </tbody>
          </Table>
        </div> */}

        <div className="card mt-3">
          <div className="card-header">
            <h4 className="mb-0">Amount Calculation</h4>
          </div>
          <div className="card-body p-0">
            <div className="list-group list-group-flush">
              <div className="list-group-item">
                <div className="row">
                  <div className="col-6">Net Total</div>
                  <div className="col-2">-</div>
                  <div className="col-4 text-end">{formatNumberWithCommas(calculationSummary.nettotal)}</div>
                </div>
              </div>

              <div className="list-group-item">
                <div className="row align-items-center">
                  <div className="col-6">Packing & Forwarding % :</div>
                  <div className="col-2">
                    <Form.Control
                      type="number"
                      value={packingandforwardingpercent}
                      onChange={(e) => setpackingandforwardingpercent(e.target.value)}
                      style={{ width: '80px' }}
                    />
                    {commonFormData.customerDetails?.packingandforwarding &&
                      <small className="text-muted"> (From customer: {commonFormData.customerDetails.packingandforwarding}%)</small>
                    }
                  </div>
                  <div className="col-4 text-end">{formatNumberWithCommas(calculationSummary.pAndF)}</div>
                </div>
              </div>

              <div className="list-group-item">
                <div className="row align-items-center">
                  <div className="col-6">Freight % :</div>
                  <div className="col-2">
                    <Form.Control
                      type="number"
                      value={freightpercent}
                      onChange={(e) => setfreightpercent(e.target.value)}
                      style={{ width: '80px' }}
                    />
                    {commonFormData.customerDetails?.freight &&
                      <small className="text-muted"> (From customer: {commonFormData.customerDetails.freight}%)</small>
                    }
                  </div>
                  <div className="col-4 text-end">{formatNumberWithCommas(calculationSummary.freight)}</div>
                </div>
              </div>

              <div className="list-group-item">
                <div className="row">
                  <div className="col-6">Sub Total :</div>
                  <div className="col-2">-</div>
                  <div className="col-4 text-end">{formatNumberWithCommas(calculationSummary.subtotal)}</div>
                </div>
              </div>

              <div className="list-group-item">
                <div className="row align-items-center">
                  <div className="col-6">GST % :</div>
                  <div className="col-2">
                    <Form.Control
                      type="number"
                      value={gstpercent}
                      onChange={(e) => setgstpercent(e.target.value)}
                      style={{ width: '80px' }}
                    />
                  </div>
                  <div className="col-4 text-end">{formatNumberWithCommas(calculationSummary.gst)}</div>
                </div>
              </div>

              <div className="list-group-item bg-light">
                <div className="row">
                  <div className="col-6"><strong>Grand Total :</strong></div>
                  <div className="col-2">-</div>
                  <div className="col-4 text-end"><strong>{formatNumberWithCommas(calculationSummary.grandtotalinr)}</strong></div>
                </div>
              </div>

              <div className="list-group-item">
                <div className="row">
                  <div className="col-8"><strong>Grand Total in Words</strong></div>
                  <div className="col-4"><em>{numberToWordsWithRupees(calculationSummary.grandtotalinr)}</em></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button className="btn btn-primary mt-3" type="submit">SUBMIT</button>
      </Form>
    </div>
  );
}

export default CreateAraiMfgIntSalesOrder;
